import { useEffect, useRef, useState } from "react";
import { fetchItemAssignees, type ConsoleAssigneeSuggestion, type ItemEditDirectoryOption } from "../api";
import { C } from "../tokens";
import { FieldCombobox } from "./FieldCombobox";
import { AssignToModal } from "./AssignToModal";
import { assigneeMode, preferWorkspaceSuggestion } from "./assigneeSuggestion";
import { initialsOf } from "./chrome";
import { AssigneeSourceBadge } from "../../../../backend/src/frontend/components/shared/assignee/AssigneeSourceBadge";
import { assigneeSourceFor, preferWorkspaceCandidate } from "../../../../backend/src/frontend/components/shared/assignee/assigneeIdentity";

// Coalesce concurrent identity reads across review cards for the same project.
const identityReads = new Map<string, Promise<ItemEditDirectoryOption[]>>();
function lookupIdentities(jobSiteId: string, category: string | undefined, name: string, search: string, visitId?: string | null) {
  const key = JSON.stringify([jobSiteId, category, name, search, visitId]);
  let pending = identityReads.get(key);
  if (!pending) {
    pending = fetchItemAssignees(jobSiteId, category, name, search, visitId);
    identityReads.set(key, pending);
    void pending.then(() => identityReads.delete(key), () => identityReads.delete(key));
  }
  return pending;
}
function mergeIdentities(previous: ItemEditDirectoryOption[], next: ItemEditDirectoryOption[]) {
  return [...new Map([...previous, ...next]
    .map(person => [person.remoteId, person])).values()];
}

/**
 * The assignee control shared by review cards and item details.
 *
 * Opening loads LedAI teammates followed by project provider people. Search
 * filters the merged directory, with shared identities displayed as LedAI.
 * Once selected, the person is shown as the same durable identity
 * pill used by the mobile review UI, rather than a string that looks editable
 * but no longer identifies the selected remote user.
 *
 * Every field opens the shared searchable combobox. When ranked suggestions
 * exist, its footer also opens the Assign-to sheet so the reviewer can inspect
 * confidence scores and compare candidates before committing with Done.
 *
 * ## The resting row has three states, not two
 *
 * Set, unset — and SUGGESTING: candidates exist and none of them won. The
 * phone draws that as a stack of dashed, ghosted initials, and so does this,
 * because the alternative is what the console did before, which was to lock a
 * pill around whatever the superintendent happened to say ("Architect", "the
 * electrician") as though it were a person in the directory. Which state is
 * live is `assigneeDisplay`'s answer, not this file's — see
 * `assigneeSuggestion.ts`.
 */
export function AssigneePicker({
  directoryEnabled = true,
  multiple = false,
  additionalAssignees = [],
  jobSiteId,
  visitId,
  category,
  search,
  label = "Assignee",
  value,
  suggestion: storedSuggestion,
  selectedRemoteId,
  typeLabel,
  align = "end",
  valueStyle,
  wrapName = false,
  showAvatar = true,
  variant = "default",
  onChange,
  testId,
  forceOpen = false,
  onOpenChange,
}: {
  directoryEnabled?: boolean;
  multiple?: boolean;
  additionalAssignees?: { remoteId: string; name: string }[];
  jobSiteId: string;
  visitId?: string | null;
  category: string | undefined;
  search: string;
  label?: string;
  value: string;
  /** The assignee match behind `value`, when a directory resolution ran. */
  suggestion?: ConsoleAssigneeSuggestion;
  /**
   * A directory id chosen in THIS editing session, if one has been.
   *
   * It has to be passed in, because a pick the reader just made is not on the
   * record yet: `suggestion` is what the server last resolved, and without
   * this the field read its own new selection as an unresolved one and fell
   * straight back to "Unassigned" the moment somebody chose. The caller
   * already tracks it — it is the reference the patch will carry.
   *
   * `undefined` means no decision yet (fall back to the record). `""` is a
   * decision: the reader cleared it, and the face-cards come back.
   */
  selectedRemoteId?: string;
  /** The record's kind, for the modal's eyebrow. */
  typeLabel?: string;
  /**
   * Which edge the value sits on.
   *
   * `"end"` is the review field TABLE, where every value is right-aligned
   * against its label. `"start"` is the properties COLUMN, where they are
   * left-aligned — and where a right-aligned assignee was the one row in
   * five that did not line up with the others. The difference was invisible
   * while the row held a short pill and obvious the moment it held a
   * face-stack.
   */
  align?: "start" | "end";
  /** Match the surrounding property's typography and control spacing. */
  valueStyle?: React.CSSProperties;
  /** Item details show the entire name, even in a narrow properties column. */
  wrapName?: boolean;
  /** The 23px initials avatar inside the locked chip. Off when the row it
   *  sits in already leads with the assignee's avatar (`PropertyRows`). */
  showAvatar?: boolean;
  /** Bare control inside the property panel's shared icon-and-value pill. */
  variant?: "default" | "property";
  onChange: (value: string, reference?: ItemEditDirectoryOption) => void;
  testId?: string;
  forceOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const scope = JSON.stringify([jobSiteId, category, search, visitId]);
  const [directory, setDirectory] = useState<{ scope: string; options: ItemEditDirectoryOption[]; identities: ItemEditDirectoryOption[] }>({ scope, options: [], identities: [] });
  const [choice, setChoice] = useState<{ scope: string; option: ItemEditDirectoryOption | null } | null>(null);
  const options = directory.scope === scope ? directory.options : [];
  const identities = directory.scope === scope ? directory.identities : [];
  const chosenOption = choice?.scope === scope ? choice.option : null;
  const setChosenOption = (option: ItemEditDirectoryOption | null) => setChoice({ scope, option });
  const setOptions = (next: ItemEditDirectoryOption[]) => setDirectory(current => ({
    scope, options: next, identities: mergeIdentities(current.scope === scope ? current.identities : [], next),
  }));
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const requestId = useRef(0);
  const selectionId = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => () => { selectionId.current += 1; }, [scope]);

  const suggestion = preferWorkspaceSuggestion(storedSuggestion, identities);
  const resolvedMode = assigneeMode({ suggestion, value, selectedRemoteId });
  const canonicalDraft = selectedRemoteId ? preferWorkspaceCandidate({ remoteId: selectedRemoteId, name: value }, identities) : undefined;
  const effectiveId = canonicalDraft?.remoteId ?? selectedRemoteId ?? suggestion?.selectedRemoteId ?? suggestion?.selected?.remoteId;
  const selectedOption = [...identities, ...options].find(option => option.remoteId === effectiveId) ??
    (chosenOption?.remoteId === effectiveId ? chosenOption : undefined);
  // The spoken field may still say only "Dave" after a directory match.
  // Display the saved identity's name, and never borrow a different person's
  // name after the reviewer changes or clears the selected id.
  const selectedCandidate = [suggestion?.selected, ...(suggestion?.candidates ?? [])]
    .find(candidate => candidate?.remoteId === effectiveId);
  const candidates = directoryEnabled ? suggestion?.candidates ?? [] : [];
  const mode = directoryEnabled ? resolvedMode : value.trim() ? "locked" : "empty";
  const selectedSource = directoryEnabled ? assigneeSourceFor(effectiveId, selectedOption?.source ?? selectedCandidate?.source, "procore") : undefined;
  const displayValue = directoryEnabled ? selectedOption?.name ?? selectedCandidate?.name ?? value : value;

  const candidateIdentityKey = JSON.stringify([
    storedSuggestion?.selected, ...(storedSuggestion?.candidates ?? []),
  ].filter(person => person && person.source !== "ledai").map(person => [person!.remoteId, person!.name]));
  useEffect(() => {
    const candidates = JSON.parse(candidateIdentityKey) as Array<[string, string]>;
    if (!directoryEnabled || !candidates.length) return;
    let cancelled = false;
    // Query the names only to discover verified id aliases. Equality is always
    // by canonical/provider id, including people outside the initial page.
    void Promise.all([...new Set(candidates.map(([, name]) => name))]
      .map(name => lookupIdentities(jobSiteId, category, name, search, visitId).catch(() => [])))
      .then(results => {
        if (cancelled) return;
        setDirectory(current => ({
          scope,
          options: current.scope === scope ? current.options : [],
          identities: mergeIdentities(current.scope === scope ? current.identities : [], results.flat()),
        }));
      });
    return () => { cancelled = true; };
  }, [scope, candidateIdentityKey, jobSiteId, category, search, directoryEnabled]);

  useEffect(() => {
    if (forceOpen) { setOpen(true); setModalOpen(multiple); }
  }, [forceOpen, multiple]);

  useEffect(() => {
    const text = query.trim();
    if (!open || !directoryEnabled) {
      setOptions([]);
      setLoading(false);
      setFailed(false);
      return;
    }
    const id = ++requestId.current;
    setOptions([]);
    setLoading(true);
    setFailed(false);
    const timer = window.setTimeout(() => {
      void fetchItemAssignees(jobSiteId, category, text, search, visitId)
        .then((next) => {
          if (requestId.current === id) setOptions(next);
        })
        .catch(() => {
          if (requestId.current === id) {
            setOptions([]);
            setFailed(true);
          }
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, 180);
    return () => {
      window.clearTimeout(timer);
      requestId.current += 1;
    };
  }, [category, jobSiteId, open, query, search, directoryEnabled]);

  const close = () => {
    setOpen(false);
    setModalOpen(false);
    setQuery("");
    setOptions([]);
    setLoading(false);
    setFailed(false);
    onOpenChange?.(false);
  };
  const show = () => {
    setModalOpen(multiple);
    setOpen(true);
    onOpenChange?.(true);
  };
  const pick = (option: ItemEditDirectoryOption) => {
    selectionId.current += 1;
    setChosenOption(option);
    onChange(option.name, option);
    close();
  };
  const clear = () => {
    selectionId.current += 1;
    setChosenOption(null);
    // Preserve the fact that this was an explicit removal.  The review patch
    // serializes this empty remote id as `selectedAssigneeRemoteId: null`,
    // which prevents the server from retaining an old auto-resolution.
    onChange("", { remoteId: "", name: "", ...(multiple ? { additionalAssignees: [] } : {}) });
    setQuery("");
    setOptions([]);
    setFailed(false);
    // The dropdown reopens on a clear so the next name can be typed straight
    // away; the modal does NOT, because clearing there is a decision the
    // footer button already announced and the sheet stays up to show it took.
    if (!modalOpen) show();
  };
  const property = variant === "property";

  const control = <FieldCombobox
    showIndicator={directoryEnabled && !property}
    label={label} triggerLabel={mode === "locked" ? `Change ${label}` : label}
    value={mode === "locked" ? effectiveId ?? "" : ""}
    options={options.map(option => ({
      value: option.remoteId, label: option.name,
      detail: [option.company, option.jobTitle, option.email].filter(Boolean).join(" · "),
      badge: <AssigneeSourceBadge source={option.source} />,
    }))}
    open={open && !modalOpen}
    onOpenChange={next => next ? show() : close()}
    query={query} onQueryChange={setQuery} filterOptions={false}
    loading={loading} error={failed ? "Could not load assignees. Try again." : undefined}
    emptyText={query.trim() ? `No people match “${query.trim()}”.` : directoryEnabled ? "No eligible assignees found." : "Type a name to assign."}
    onCreate={!directoryEnabled ? name => pick({ remoteId: "", name }) : undefined}
    createOption={name => ({ label: name, icon: <AssigneeInitials name={name} /> })}
    onChange={id => {
      if (id === null) { clear(); close(); return; }
      const person = options.find(option => option.remoteId === id);
      if (person) pick(person);
    }}
    triggerTestId={testId} panelTestId={`${testId ?? "assignee"}-panel`}
    inputTestId={`${testId ?? "assignee"}-search`} align={align}
    triggerRef={triggerRef} restoreFocus={!modalOpen}
    triggerStyle={{
      justifyContent: align === "start" ? "flex-start" : "flex-end",
      textAlign: align === "start" ? "left" : "right",
      fontSize: align === "start" ? 15.52 : 16.67,
      fontWeight: mode === "locked" ? 700 : align === "start" ? 500 : 650,
      ...valueStyle,
      color: mode === "locked" ? C.ink : align === "start" ? C.propEmpty : C.sub2,
      ...(mode === "locked" && !property ? { padding: "0 5px 0 0", marginLeft: 0, border: "none", width: "auto" } : {}),
    }}
    footer={candidates.length > 0 ? <button
      type="button" data-testid={`${testId ?? "assignee"}-review-matches`}
      onClick={() => { setQuery(""); setModalOpen(true); }}
      style={{ flexShrink: 0, padding: "10px", marginTop: 4, border: "none", borderTop: `1px solid ${C.line}`, background: "transparent", color: C.ink, textAlign: "left", font: "inherit", fontSize: 13.8, cursor: "pointer" }}
    >Review suggested matches</button> : undefined}
  >
    {mode === "locked" ? <>
      {showAvatar && <AssigneeInitials name={displayValue} />}
      <span style={wrapName
        ? { minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere", textAlign: "left" }
        : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayValue}{additionalAssignees.length > 0 ? ` +${additionalAssignees.length}` : ""}</span>
      <AssigneeSourceBadge source={selectedSource} />
    </> : <>
      {mode === "suggesting" && <GhostStack names={candidates.map(one => one.name)} />}
      <span>{`Add ${label.toLowerCase()}`}</span>
    </>}
  </FieldCombobox>;

  return (
    <div style={{ position: "relative", flex: property ? "0 1 auto" : 1, minWidth: 0, display: "flex", justifyContent: align === "start" ? "flex-start" : "flex-end" }}>
      <span className={!directoryEnabled && !property && mode === "locked" ? "assignee-name-pill" : undefined} style={property
        ? { display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, maxWidth: "100%" }
        : mode === "locked"
        ? { display: "inline-flex", alignItems: "center", minWidth: 0, maxWidth: "100%", border: `1px solid ${C.line2}`, borderRadius: wrapName ? 16 : 999, boxSizing: "border-box", padding: showAvatar ? "3px 5px 3px 4px" : "3px 5px 3px 6px", background: C.faint, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.42)" }
        : { display: "inline-flex", minWidth: 0, width: "100%" }}>
        {control}
        {mode === "locked" && <button type="button" aria-label={`Clear ${label}`} onClick={clear} className={property ? "property-clear" : undefined}
          style={{ width: property ? 18 : 24, height: property ? 18 : 24, flex: "none", border: "none", borderRadius: "50%", background: property ? C.propClear : C.line2, color: property ? C.surface : C.sub, display: "grid", placeItems: "center", padding: 0, cursor: "pointer", fontSize: property ? 16 : 20.7, lineHeight: 1 }}>
          ×
        </button>}
      </span>

      {open && modalOpen && (
        <AssignToModal
          multiple={multiple ? { selected: [
            ...(mode === "locked" && effectiveId ? [{ remoteId: effectiveId, name: displayValue }] : []),
            ...additionalAssignees.map(person => identities.find(option => option.remoteId === person.remoteId || option.providerRemoteIds?.includes(person.remoteId)) ?? person),
          ].filter((person, index, all) => all.findIndex(other => other.remoteId === person.remoteId) === index) } : undefined}
          label={label}
          typeLabel={typeLabel}
          suggestion={suggestion}
          value={mode === "locked" ? displayValue : ""}
          query={query}
          onQueryChange={setQuery}
          options={options}
          isLoading={loading}
          failed={failed}
          // Only while somebody is actually assigned. A record can carry a
          // stored `selectedAssigneeRemoteId` whose assignee has since been
          // cleared, and marking that row SET would announce an assignment
          // the field itself is reporting as gone.
          committedRemoteId={
            mode === "locked" ? effectiveId : undefined
          }
          // The sheet drafts; this applies. Both paths already close it, so
          // neither closes here.
          onCommit={async (choice, selections) => {
            const id = ++selectionId.current;
            let selected = choice && identities.find((person) => person.remoteId === choice.remoteId);
            // A suggestion can be confirmed before its identity lookup finishes.
            // Wait for that same lookup so company/trade metadata is not lost.
            if (choice && !selected) {
              const people = await lookupIdentities(jobSiteId, category, choice.name, search, visitId).catch(() => []);
              selected = people.find((person) => person.remoteId === choice.remoteId || person.providerRemoteIds?.includes(choice.remoteId)) ?? choice;
            }
            const extras = selections ? await Promise.all(selections.slice(1).map(async person => {
              const known = identities.find(option => option.remoteId === person.remoteId || option.providerRemoteIds?.includes(person.remoteId));
              if (known) return known;
              const people = await lookupIdentities(jobSiteId, category, person.name, search, visitId).catch(() => []);
              return people.find(option => option.remoteId === person.remoteId || option.providerRemoteIds?.includes(person.remoteId)) ?? person;
            })) : [];
            if (id !== selectionId.current) return;
            if (selected && multiple) selected = { ...selected, additionalAssignees: extras
              .filter((person, index, all) => person.remoteId !== selected!.remoteId && all.findIndex(other => other.remoteId === person.remoteId) === index)
              .map(({ remoteId, name }) => ({ remoteId, name })) };
            setChosenOption(selected ?? null);
            selected ? onChangeRef.current(selected.name, selected) : onChangeRef.current("", { remoteId: "", name: "", ...(multiple ? { additionalAssignees: [] } : {}) });
          }}
          onClose={() => { close(); triggerRef.current?.focus(); }}
          testId={`${testId ?? "assignee"}-assign-to`}
        />
      )}


    </div>
  );
}

function AssigneeInitials({ name }: { name: string }) {
  return <span aria-hidden data-testid="assignee-initials" style={{
    width: 23, height: 23, flex: "none", display: "grid", placeItems: "center",
    borderRadius: "50%", background: C.ink, color: C.surface, fontSize: 10.93, fontWeight: 800,
  }}>{initialsOf(name)}</span>;
}

/**
 * Ghosted, dashed faces — candidates exist and nobody has been chosen.
 *
 * The phone's `GhostStack`, at the console's scale: dashed rather than filled
 * because a solid avatar is a claim that a person is assigned, and overlapped
 * because three separate discs read as three assignees instead of one
 * unresolved question. Three shown, then a `+n`.
 */
function GhostStack({ names }: { names: string[] }) {
  const shown = names.slice(0, names.length > 3 ? 2 : 3);
  const overflow = names.length - shown.length;
  const disc: React.CSSProperties = {
    width: 23,
    height: 23,
    flex: "none",
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    border: `1.5px dashed ${C.line2}`,
    background: C.surface,
    color: C.sub2,
    fontSize: 9.77,
    fontWeight: 800,
  };
  return (
    <span
      aria-hidden
      data-testid="assignee-ghost-stack"
      style={{ display: "inline-flex", flex: "none" }}
    >
      {shown.map((name, index) => (
        <span key={`${name}:${index}`} style={{ ...disc, marginLeft: index ? -8 : 0 }}>
          {initialsOf(name)}
        </span>
      ))}
      {overflow > 0 && (
        <span style={{ ...disc, marginLeft: -8 }}>+{overflow}</span>
      )}
    </span>
  );
}
